import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { 
  collection, query, where, onSnapshot, addDoc, doc, deleteDoc
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  IoCheckboxOutline, IoAddOutline, IoTrashOutline, IoPeopleOutline, IoCalendarOutline, IoArrowBackOutline
} from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import './HomeScreen.css';

function AdminActividadesScreen() {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSaaS = location.pathname.includes('/superuser/hr');
  const activeCondominioId = isSaaS ? 'SAAS' : userData?.condominioId;

  const [empleados, setEmpleados] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [actividades, setActividades] = useState([]);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [periodicidad, setPeriodicidad] = useState('Diaria');
  const [fechaAsignada, setFechaAsignada] = useState('');
  const [semanalDias, setSemanalDias] = useState([]);
  const [mensualDias, setMensualDias] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch employees
  useEffect(() => {
    if (!activeCondominioId) return;

    const q = query(
      collection(db, "empleados"),
      where("condominioId", "==", activeCondominioId)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEmpleados(list);
      if (list.length > 0 && !selectedEmp) {
        setSelectedEmp(list[0]);
      }
    });
    return () => unsubscribe();
  }, [activeCondominioId]);

  // Fetch activities for selected employee
  useEffect(() => {
    if (!selectedEmp?.usuarioId || !activeCondominioId) {
      setActividades([]);
      return;
    }

    const q = query(
      collection(db, "actividades_diarias"),
      where("condominioId", "==", activeCondominioId),
      where("trabajadorId", "==", selectedEmp.usuarioId)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setActividades(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [selectedEmp, activeCondominioId]);

  const handleAddActividad = async (e) => {
    e.preventDefault();
    if (!nombre || !selectedEmp) return;

    setLoading(true);
    try {
      await addDoc(collection(db, "actividades_diarias"), {
        nombre,
        descripcion,
        periodicidad,
        fechaAsignada: periodicidad === 'Una Sola Vez' ? fechaAsignada : null,
        semanalDias: periodicidad === 'Semanal' ? semanalDias : [],
        mensualDias: periodicidad === 'Mensual' ? mensualDias : [],
        trabajadorId: selectedEmp.usuarioId,
        trabajadorNombre: `${selectedEmp.nombre} ${selectedEmp.apellidos}`,
        condominioId: activeCondominioId,
        createdAt: new Date()
      });
      setShowModal(false);
      setNombre('');
      setDescripcion('');
      setPeriodicidad('Diaria');
      setFechaAsignada('');
      setSemanalDias([]);
      setMensualDias([]);
    } catch (error) {
      console.error(error);
      alert("Error al agregar la actividad.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteActividad = async (actId) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta actividad?")) return;
    try {
      await deleteDoc(doc(db, "actividades_diarias", actId));
    } catch (error) {
      console.error(error);
      alert("Error al eliminar la actividad.");
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/nominas-dashboard" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          <style>{`
            .ks-responsive-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 20px;
            }
            @media (min-width: 768px) {
              .ks-responsive-grid {
                grid-template-columns: 280px 1fr;
                gap: 30px;
              }
            }
          `}</style>

          {/* Header Hero */}
          <div className="muro-header-stitch" style={{ marginBottom: '20px' }}>
            <div className="muro-hero-card" style={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '15px 20px',
              minHeight: 'auto',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', gap: '15px', zIndex: 2 }}>
                <button 
                  onClick={() => navigate(isSaaS ? '/superuser/hr/dashboard' : '/nominas-dashboard')} 
                  className="usuarios-back-btn ks-back-btn-reco glass" 
                  title="Volver" 
                  style={{ margin: 0, position: 'relative', flexShrink: 0 }}
                >
                  <IoArrowBackOutline size={24} />
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                  <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.2, color: 'white', whiteSpace: 'normal', wordBreak: 'break-word' }}>Actividades de Personal</h1>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.8)', whiteSpace: 'normal', wordBreak: 'break-word' }}>Asignación de tareas recurrentes y únicas para trabajadores operativos</p>
                </div>
              </div>
              <IoCheckboxOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            <div className="ks-responsive-grid">
              
              {/* Sidebar: Empleados */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: 'fit-content' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b' }}>
                  <IoPeopleOutline size={18} />
                  <span>Personal Operativo</span>
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {empleados.map(emp => {
                    const active = selectedEmp?.id === emp.id;
                    return (
                      <button
                        key={emp.id}
                        onClick={() => setSelectedEmp(emp)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px',
                          borderRadius: '12px',
                          border: active ? '1px solid #6366f1' : '1px solid #f1f5f9',
                          background: active ? '#f5f3ff' : 'white',
                          color: active ? '#4f46e5' : '#475569',
                          fontWeight: active ? '700' : '500',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px'
                        }}
                      >
                        <span style={{ fontSize: '0.9rem' }}>{emp.nombre} {emp.apellidos}</span>
                        <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>{emp.puesto}</span>
                      </button>
                    );
                  })}
                  {empleados.length === 0 && <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No hay empleados registrados.</p>}
                </div>
              </div>

              {/* Main Area: Actividades */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                {selectedEmp ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', color: '#1e293b' }}>
                          Tareas de {selectedEmp.nombre}
                        </h3>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>{selectedEmp.puesto}</p>
                      </div>
                      <button 
                        onClick={() => setShowModal(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: '#6366f1',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        <IoAddOutline size={18} />
                        <span>Nueva Actividad</span>
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {actividades.map(act => (
                        <div 
                          key={act.id} 
                          style={{
                            padding: '16px',
                            borderRadius: '12px',
                            border: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '20px',
                                background: act.periodicidad === 'Una Sola Vez' ? '#fee2e2' : '#e0e7ff',
                                color: act.periodicidad === 'Una Sola Vez' ? '#ef4444' : '#4f46e5',
                                fontSize: '0.7rem',
                                fontWeight: '700'
                              }}>{act.periodicidad}</span>
                              {act.fechaAsignada && (
                                <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <IoCalendarOutline size={12} /> {act.fechaAsignada}
                                </span>
                              )}
                              {act.periodicidad === 'Semanal' && act.semanalDias && act.semanalDias.length > 0 && (
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  ({act.semanalDias.join(', ')})
                                </span>
                              )}
                              {act.periodicidad === 'Mensual' && act.mensualDias && act.mensualDias.length > 0 && (
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  (Días: {act.mensualDias.join(', ')})
                                </span>
                              )}
                            </div>
                            <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', color: '#1e293b' }}>{act.nombre}</h4>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>{act.descripcion}</p>
                          </div>
                          <button 
                            onClick={() => handleDeleteActividad(act.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              padding: '8px'
                            }}
                          >
                            <IoTrashOutline size={18} />
                          </button>
                        </div>
                      ))}
                      {actividades.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                          <IoCheckboxOutline size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                          <p style={{ margin: 0, fontSize: '0.9rem' }}>No hay actividades asignadas a este empleado.</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p>Selecciona un empleado para gestionar sus actividades.</p>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* Modal: Agregar Actividad */}
        {showModal && (
          <div className="notifications-overlay" onClick={() => setShowModal(false)}>
            <div className="notifications-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
              <div className="notifications-modal-header">
                <h2>Nueva Actividad</h2>
                <button className="close-modal-btn" onClick={() => setShowModal(false)}>X</button>
              </div>
              <form onSubmit={handleAddActividad} style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Nombre de la Actividad *</label>
                  <input 
                    type="text" 
                    value={nombre} 
                    onChange={e => setNombre(e.target.value)} 
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                    placeholder="Ej. Limpieza de Alberca"
                    required 
                  />
                </div>
                
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Detalles / Descripción</label>
                  <textarea 
                    value={descripcion} 
                    onChange={e => setDescripcion(e.target.value)} 
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', minHeight: '80px' }} 
                    placeholder="Detalles sobre cómo realizar la tarea"
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Periodicidad</label>
                  <select 
                    value={periodicidad} 
                    onChange={e => setPeriodicidad(e.target.value)} 
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                  >
                    <option value="Diaria">Diaria</option>
                    <option value="Semanal">Semanal</option>
                    <option value="Mensual">Mensual</option>
                    <option value="Una Sola Vez">Una Sola Vez (One-timer)</option>
                  </select>
                </div>

                 {periodicidad === 'Una Sola Vez' && (
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Fecha de Asignación</label>
                    <input 
                      type="date" 
                      value={fechaAsignada} 
                      onChange={e => setFechaAsignada(e.target.value)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                      required
                    />
                  </div>
                )}

                {periodicidad === 'Semanal' && (
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Días de la Semana *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map(d => {
                        const active = semanalDias.includes(d);
                        return (
                          <button
                            type="button"
                            key={d}
                            onClick={() => {
                              if (active) {
                                setSemanalDias(semanalDias.filter(item => item !== d));
                              } else {
                                setSemanalDias([...semanalDias, d]);
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '20px',
                              border: active ? '1px solid #6366f1' : '1px solid #cbd5e1',
                              background: active ? '#6366f1' : 'white',
                              color: active ? 'white' : '#475569',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {periodicidad === 'Mensual' && (
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Días del Mes *</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(num => {
                        const active = mensualDias.includes(num);
                        return (
                          <button
                            type="button"
                            key={num}
                            onClick={() => {
                              if (active) {
                                setMensualDias(mensualDias.filter(item => item !== num));
                              } else {
                                setMensualDias([...mensualDias, num]);
                              }
                            }}
                            style={{
                              padding: '6px 0',
                              borderRadius: '6px',
                              border: active ? '1px solid #6366f1' : '1px solid #cbd5e1',
                              background: active ? '#6366f1' : 'white',
                              color: active ? 'white' : '#475569',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              textAlign: 'center'
                            }}
                          >
                            {num}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={loading}
                  style={{
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '10px'
                  }}
                >
                  {loading ? 'Guardando...' : 'Asignar Actividad'}
                </button>
              </form>
            </div>
          </div>
        )}

      </main>
      <KondoBottomNav activeRoute="/nominas-dashboard" />
    </div>
  );
}

export default AdminActividadesScreen;
